import CkEditorContentStyles from '../styles/ckEditorContent';
import React, { ReactElement, Suspense } from 'react';
import ReactPlayer from 'react-player';
import { Box } from '@mui/material';
import { sendGeneralContentVideoDetails } from '../../common/utils/gaEvents';
import '../styles/ckeditor.css';
import { Skeleton } from '@mui/lab';
import { clearUserSessionTimers, useActivateUserSessionTimers } from '../utils/userSessionTimers';
import { useAppSelector } from '../../store/hook';
import { appendCdnToImagePath } from '../utils/appendCdnToImagePath';
import ReactHtmlParser, { DOMNode } from 'html-react-parser';

// Using require (provided by webpack) because import does not work for undeclared modules.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { MATCH_URL_YOUTUBE } = require('react-player/lib/patterns');

/** lazy loading video components */
const VideoPlayer = React.lazy(() => import('./VideoPlayer'));

interface ContentContainerProps {
  content: string;
  pageStartTime?: Date;
  generalContentName?: string;
  taskName?: string;
}

const CkEditorContent: React.FC<ContentContainerProps> = (props: ContentContainerProps): React.JSX.Element => {
  const { content } = props;
  const template = useAppSelector((state) => state.template);
  const { classes } = CkEditorContentStyles();
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const candidate = useAppSelector((state) => state.candidate);
  const activateUserSessionTimers = useActivateUserSessionTimers();

  /** Video Callback methods */
  const onVideoPlay = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('VideoPlayed:', currentRef.current?.getCurrentTime());
    if (userSessionActive) {
      clearUserSessionTimers();
      activateUserSessionTimers();
    }
  };

  const onVideoPause = (currentRef: React.RefObject<ReactPlayer>, url: string): void => {
    console.log('VideoPaused:', currentRef.current?.getCurrentTime(), currentRef.current);
    const currentDuration = currentRef.current?.getCurrentTime();
    const totalDuration = currentRef.current?.getDuration();
    /** GA Event on Video pause */
    sendGeneralContentVideoDetails(
      currentDuration,
      totalDuration,
      props.pageStartTime || new Date(),
      url,
      props.generalContentName,
      props.taskName,
      { template, candidate },
    );
  };

  const getTotalVideoDuration = (currentRef: React.RefObject<ReactPlayer>): void => {
    console.log('TotalVideoDuration', currentRef.current?.getDuration());
  };

  const getVideoEnd = (currentRef: React.RefObject<ReactPlayer>, url: string): void => {
    console.log('Video Ended:', currentRef.current?.getCurrentTime());
    const currentDuration = currentRef.current?.getCurrentTime();
    const totalDuration = currentRef.current?.getDuration();
    /** GA Event : on video end */
    sendGeneralContentVideoDetails(
      currentDuration,
      totalDuration,
      props.pageStartTime || new Date(),
      url,
      props.generalContentName,
      props.taskName,
      { template, candidate },
    );
  };

  const renderVideo = (url: string, index: number): ReactElement => (
    <Suspense key={index} fallback={<Skeleton height="100vh" />}>
      <VideoPlayer
        url={url}
        lightMode={true}
        onVideoPlay={onVideoPlay}
        onVideoPause={onVideoPause}
        getTotalVideoDuration={getTotalVideoDuration}
        getVideoEnd={getVideoEnd}
      />
    </Suspense>
  );

  const renderCustomVideo = (url: string, captionPath: string, transcriptPath: string, index: number): ReactElement => (
    <Suspense key={index} fallback={<Skeleton height="100vh" />}>
      <VideoPlayer
        url={url.includes('https://') ? url.replace('https://', '') : url}
        captionPath={captionPath ? appendCdnToImagePath(captionPath) : ''}
        transcriptPath={transcriptPath ? appendCdnToImagePath(transcriptPath) : ''}
        lightMode={false}
        onVideoPlay={onVideoPlay}
        onVideoPause={onVideoPause}
        getTotalVideoDuration={getTotalVideoDuration}
        getVideoEnd={getVideoEnd}
      />
    </Suspense>
  );

  const checkVideoSource = (url: string, index: number): ReactElement => {
    if (url.includes('vimeo') || url.match(MATCH_URL_YOUTUBE)) {
      return renderVideo(url, index);
    } else {
      const [videoData, transcriptPath = ''] = url.split('-transcriptPath:');
      const [videoUrl, captionPath = ''] = videoData.split('-captionPath:');
      return renderCustomVideo(videoUrl, captionPath, transcriptPath, index);
    }
  };

  const replace = (node: DOMNode, index: number): JSX.Element | undefined => {
    if (node.type === 'tag' && node.name === 'oembed') {
      const url = node.attribs && node.attribs.url ? node.attribs.url : '';
      return checkVideoSource(url, index);
    }
  };

  return (
    <>
      {content && (
        <Box className={`${classes.hyphenate} ck-content`} data-testid="ckeditor-text">
          <div>{ReactHtmlParser(content, { replace })}</div>
        </Box>
      )}
    </>
  );
};

export default CkEditorContent;
